import { useState } from "react";

import { OrganizationTelephony, TwilioCredentials, TelnyxCredentials, SbcCredentials } from "../../redux/reducers/organizationTelephonySlice";
import { Button, DropdownField, InputField, SettingsList, SettingsListItem, Spinner, Text, View } from "../base";
import { Dialog } from "../base/Dialog";
import { Icon } from "../base/Icon";
import { Form, useForm } from "../base/Form";
import { TelephonyProviderDropdown } from "./TelephonyProviderDropdown";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";
import { capitalize } from "../../utils/strings";
import { ConfirmDialog } from "../ConfirmDialog";
import { IconButton } from "../base/IconButton";

type TelephonyCardProps = {
  telephonyConfigs: OrganizationTelephony[];
  onAdd: (config: Partial<OrganizationTelephony>) => void;
  onUpdate: (config: Partial<OrganizationTelephony>) => void;
  onDelete: (id: string) => void;
  className?: string;
  isLoading?: boolean;
  isAdmin?: boolean;
};

type ProviderField = {
  name: string;
  label: string;
  placeholder: string;
  type?: "text" | "password";
  required?: boolean;
  helpText?: string;
};

const PROVIDER_FIELDS: Record<string, ProviderField[]> = {
  voicerun: [],
  telnyx: [
    {
      name: "apiKey",
      label: "API Key",
      placeholder: "Enter Telnyx API Key",
      type: "password",
      required: true,
    },
  ],
  twilio: [
    {
      name: "accountSid",
      label: "Account SID",
      placeholder: "Enter Twilio Account SID",
      required: true,
    },
    {
      name: "apiKeySid",
      label: "API Key SID",
      placeholder: "Enter Twilio API Key SID",
      required: true,
    },
    {
      name: "apiKeySecret",
      label: "API Key Secret",
      placeholder: "Enter Twilio API Key Secret",
      type: "password",
      required: true,
    },
  ],
  "voicerun-sbc": [
    {
      name: "baseUrl",
      label: "Base URL",
      placeholder: "http://10.20.0.2:8766",
      required: true,
    },
    {
      name: "dashboardUrl",
      label: "Dashboard URL",
      placeholder: "http://10.20.0.2:5000",
      required: true,
    },
    {
      name: "gateway",
      label: "Gateway",
      placeholder: "twilio",
    },
    {
      name: "twilioAccountSid",
      label: "Twilio Account SID",
      placeholder: "Enter Twilio Account SID",
      required: true,
    },
    {
      name: "twilioAuthToken",
      label: "Twilio Auth Token",
      placeholder: "Enter Twilio Auth Token",
      type: "password",
      required: true,
    },
    {
      name: "twilioTrunkName",
      label: "SIP Trunk Name",
      placeholder: "voicerun-sbc-development",
    },
    {
      name: "dashboardUser",
      label: "Dashboard Username",
      placeholder: "Enter dashboard username",
    },
    {
      name: "dashboardPassword",
      label: "Dashboard Password",
      placeholder: "Enter dashboard password",
      type: "password",
    },
  ],
};

const TWILIO_REGION_OPTIONS = [
  { label: "US (default)", value: "us1" },
  { label: "Australia", value: "au1" },
  { label: "Ireland", value: "ie1" },
];

type TelephonyFormData = {
  name: string;
  providerType: string;
  apiKey: string;
  accountSid: string;
  apiKeySid: string;
  apiKeySecret: string;
  region: string;
  baseUrl: string;
  dashboardUrl: string;
  gateway: string;
  twilioAccountSid: string;
  twilioAuthToken: string;
  twilioTrunkName: string;
  dashboardUser: string;
  dashboardPassword: string;
};

export const TelephonyCard = ({ telephonyConfigs, onAdd, onUpdate, onDelete, className, isLoading, isAdmin = false }: TelephonyCardProps) => {
  // SBC configs are only visible to admins
  const visibleConfigs = isAdmin ? telephonyConfigs : telephonyConfigs.filter((c) => c.providerType !== "voicerun-sbc");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState<OrganizationTelephony | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingConfig, setDeletingConfig] = useState<OrganizationTelephony | null>(null);

  const { formData, onChange } = useForm<TelephonyFormData>({
    name: "",
    providerType: "",
    apiKey: "",
    accountSid: "",
    apiKeySid: "",
    apiKeySecret: "",
    region: "us1",
    baseUrl: "",
    dashboardUrl: "",
    gateway: "",
    twilioAccountSid: "",
    twilioAuthToken: "",
    twilioTrunkName: "",
    dashboardUser: "",
    dashboardPassword: "",
  });

  const selectedProviderFields = formData.providerType ? PROVIDER_FIELDS[formData.providerType] : undefined;

  const onClickAdd = () => {
    const data = {
      name: formData.name,
      providerType: formData.providerType,
    } as Partial<OrganizationTelephony>;

    if (formData.providerType === "twilio") {
      data.credentials = {
        accountSid: formData.accountSid || "",
        apiKeySid: formData.apiKeySid || "",
        apiKeySecret: formData.apiKeySecret || "",
        region: formData.region || "us1",
      } as TwilioCredentials;
    } else if (formData.providerType === "telnyx") {
      data.credentials = {
        apiKey: formData.apiKey || "",
      } as TelnyxCredentials;
    } else if (formData.providerType === "voicerun-sbc") {
      data.credentials = {
        baseUrl: formData.baseUrl || "",
        dashboardUrl: formData.dashboardUrl || "",
        gateway: formData.gateway || "twilio",
        twilioAccountSid: formData.twilioAccountSid || "",
        twilioAuthToken: formData.twilioAuthToken || "",
        twilioTrunkName: formData.twilioTrunkName || "voicerun-sbc-development",
        dashboardUser: formData.dashboardUser || "",
        dashboardPassword: formData.dashboardPassword || "",
      } as SbcCredentials;
    }

    onAdd(data);
    setShowAddDialog(false);
    resetForm();
  };

  const onClickUpdate = () => {
    if (editingConfig) {
      onUpdate({
        id: editingConfig.id,
        name: formData.name,
      });
      setEditingConfig(null);
      resetForm();
    }
  };

  const onClickEdit = (config: OrganizationTelephony) => {
    setEditingConfig(config);
    onChange({
      name: config.name,
      providerType: config.providerType,
      apiKey: "**********",
      accountSid: "**********",
      apiKeySid: "**********",
      apiKeySecret: "**********",
      region: (config.credentials as TwilioCredentials)?.region || "us1",
      baseUrl: "**********",
      dashboardUrl: "**********",
      gateway: "**********",
      twilioAccountSid: "**********",
      twilioAuthToken: "**********",
      twilioTrunkName: "**********",
      dashboardUser: "**********",
      dashboardPassword: "**********",
    });
  };

  const resetForm = () => {
    onChange({
      name: "",
      providerType: "",
      apiKey: "",
      accountSid: "",
      apiKeySid: "",
      apiKeySecret: "",
      region: "us1",
      baseUrl: "",
      dashboardUrl: "",
      gateway: "",
      twilioAccountSid: "",
      twilioAuthToken: "",
      twilioTrunkName: "",
      dashboardUser: "",
      dashboardPassword: "",
    });
  };

  const isFormValid = () => {
    if (!formData.name || !formData.providerType) return false;

    if (formData.providerType === "twilio") {
      return formData.accountSid && formData.apiKeySid && formData.apiKeySecret;
    } else if (formData.providerType === "telnyx") {
      return formData.apiKey;
    } else if (formData.providerType === "voicerun-sbc") {
      return formData.baseUrl && formData.dashboardUrl && formData.twilioAccountSid && formData.twilioAuthToken;
    }

    return false;
  };

  const handleDeleteClick = (config: OrganizationTelephony) => {
    setDeletingConfig(config);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (deletingConfig) {
      onDelete(deletingConfig.id);
      setShowDeleteDialog(false);
      setDeletingConfig(null);
    }
  };

  return (
    <OrganizationSettingsTab
      title="Telephony"
      count={visibleConfigs.length}
    >
      {isLoading ? (
        <View className="flex items-center justify-center py-4">
          <Spinner className="!text-brand" />
        </View>
      ) : (
        <SettingsList>
          {visibleConfigs.map((config) => (
            <SettingsListItem
              key={config.id}
              title={config.name}
              subtitle={capitalize(config.providerType)}
              icon={config.providerType}
            >
              <View className="flex items-center gap-2">
                <IconButton
                  variant="ghost"
                  icon="edit"
                  onClick={() => onClickEdit(config)}
                />
                <IconButton
                  variant="danger"
                  icon="trash"
                  onClick={() => handleDeleteClick(config)}
                />
              </View>
            </SettingsListItem>
          ))}
          <button
            onClick={() => setShowAddDialog(true)}
            className="flex flex-row items-center gap-2 px-4 py-3 w-full text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
          >
            <View className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 group-hover:border-brand dark:group-hover:border-brand-light transition-colors">
              <Icon
                name="add"
                size={14}
                className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-brand-light transition-colors"
              />
            </View>
            <Text className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
              Add configuration
            </Text>
          </button>
        </SettingsList>
      )}

      <Dialog
        isOpen={showAddDialog || !!editingConfig}
        onClose={() => {
          setShowAddDialog(false);
          setEditingConfig(null);
          resetForm();
        }}
        title={editingConfig ? "Edit Telephony Configuration" : "Add Telephony Configuration"}
        size={formData.providerType === "voicerun-sbc" ? "xl" : "md"}
      >
        <Form
          data={formData}
          onChange={onChange}
          className="p-4"
        >
          <InputField
            name="name"
            label="Name"
            placeholder="Enter configuration name"
          />

          {formData.providerType && (
            <Text
              variant="muted"
              className="text-sm"
            >
              The fields below cannot be edited after the configuration is created:
            </Text>
          )}

          <TelephonyProviderDropdown
            value={formData.providerType}
            onChange={(value) => onChange({ providerType: value })}
            readOnly={!!editingConfig}
            showHelpText={true}
            isAdmin={isAdmin}
          />

          {selectedProviderFields && (
            <>
              <View className={formData.providerType === "voicerun-sbc" ? "grid grid-cols-2 gap-x-6 gap-y-2" : undefined}>
                {selectedProviderFields.map((field: ProviderField) => (
                  <InputField
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    placeholder={field.placeholder}
                    type={field.type || "text"}
                    readOnly={!!editingConfig}
                  />
                ))}
              </View>
              {formData.providerType === "twilio" && (
                <DropdownField
                  name="region"
                  label="Region"
                  options={TWILIO_REGION_OPTIONS}
                  value={formData.region || "us1"}
                  onChange={(value) => onChange({ region: value })}
                  readOnly={!!editingConfig}
                />
              )}
            </>
          )}

          <View className="flex gap-3 pt-2">
            <Button
              variant="success"
              size="sm"
              outline
              onClick={editingConfig ? onClickUpdate : onClickAdd}
              disabled={!isFormValid()}
              className="flex-1 justify-center"
            >
              <Icon
                name={editingConfig ? "check" : "add"}
                size={14}
              />
              {editingConfig ? "Update" : "Add"}
            </Button>
            <Button
              variant="neutral"
              size="sm"
              outline
              onClick={() => {
                setShowAddDialog(false);
                setEditingConfig(null);
                resetForm();
              }}
              className="flex-1 justify-center"
            >
              Cancel
            </Button>
          </View>
        </Form>
      </Dialog>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setDeletingConfig(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Telephony Configuration"
        message={`Are you sure you want to delete "${deletingConfig?.name}"?`}
      >
        <Text className="text-sm text-gray-600 dark:text-gray-400">This action cannot be undone. The telephony configuration will be permanently removed.</Text>
      </ConfirmDialog>
    </OrganizationSettingsTab>
  );
};
